import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronUp } from 'lucide-react';
import { C } from '../lib/palette';

export function Card({ children, className = '', style, ...rest }) {
  return (
    <div
      className={`rounded-lg ${className}`}
      style={{ background: C.card, border: `1px solid ${C.lineSoft}`, ...style }}
      {...rest}
    >
      {children}
    </div>
  );
}

export function Btn({ children, variant = 'primary', className = '', style, ...rest }) {
  const base = 'tap inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold transition-colors';
  const styles = {
    primary: { background: C.teal, color: '#fff' },
    plum: { background: C.plum, color: '#fff' },
    gold: { background: C.gold, color: '#fff' },
    secondary: { background: '#fff', color: C.teal, border: `1px solid ${C.line}` },
    ghost: { background: 'transparent', color: C.inkSoft },
  };
  return (
    <button className={`${base} ${className}`} style={{ ...styles[variant], ...style }} {...rest}>
      {children}
    </button>
  );
}

export function Pill({ children, tone = 'teal', className = '', ...rest }) {
  const map = {
    teal: { background: C.tealSoft, color: C.teal },
    gold: { background: C.goldSoft, color: C.gold },
    plum: { background: C.plumSoft, color: C.plum },
    line: { background: 'transparent', color: C.inkSoft, border: `1px solid ${C.line}` },
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[0.7rem] font-semibold tracking-wide ${className}`}
      style={map[tone]}
      {...rest}
    >
      {children}
    </span>
  );
}

export function SectionLabel({ children, style }) {
  return (
    <p className="text-[0.68rem] font-semibold uppercase tracking-[0.16em]" style={{ color: C.line, ...style }}>
      {children}
    </p>
  );
}

// Permanent "Why this design" line each product shows on its main screen.
export function WhyLine({ children }) {
  return (
    <p
      data-testid="why-this-design"
      className="rounded-md px-3 py-2.5 text-xs leading-relaxed"
      style={{ background: C.tealSoft, color: C.teal, borderLeft: `3px solid ${C.teal}` }}
    >
      {children}
    </p>
  );
}

export function EmptyState({ title, body, action, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-lg px-6 py-10 text-center"
      style={{ border: `1px dashed ${C.line}` }}
    >
      <h3 className="font-serif text-lg" style={{ color: C.ink }}>{title}</h3>
      {body && <p className="mt-1 text-sm" style={{ color: C.inkSoft }}>{body}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

// Slim header with a real back button on every screen except the Hub.
export function Header({ title, right }) {
  const nav = useNavigate();
  const loc = useLocation();
  const goBack = () => {
    if (loc.key && loc.key !== 'default' && window.history.length > 2) nav(-1);
    else nav('/');
  };
  return (
    <header
      className="safe-top sticky top-0 z-40 flex items-center gap-2 px-3 py-2.5"
      style={{ background: 'rgba(243,245,241,0.92)', backdropFilter: 'blur(8px)', borderBottom: `1px solid ${C.lineSoft}` }}
    >
      <button data-testid="back-button" onClick={goBack} className="tap flex items-center gap-1 rounded-md px-2 text-sm font-medium" style={{ color: C.inkSoft }}>
        <ArrowLeft size={18} /> Back
      </button>
      {title && <span className="font-serif text-base" style={{ color: C.ink }}>{title}</span>}
      <div className="ml-auto">{right}</div>
    </header>
  );
}

export function TopButton() {
  const [show, setShow] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 500);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!show) return null;
  return (
    <button
      data-testid="compass-top-button"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="safe-bottom tap fixed bottom-4 right-4 z-40 flex items-center gap-1 rounded-full px-3 py-2 text-xs font-semibold shadow-lg"
      style={{ background: C.teal, color: '#fff' }}
    >
      <ChevronUp size={16} /> Top
    </button>
  );
}
